import { PauseCircle, Timer } from 'lucide-react'
import { Chip, Notice } from '../ui'
import { useRoleLabels } from '../roleLabels'
import { date, dateTime, pluralise, words } from '../format'

// ======================================================================
// HEADER AND STAGE RAIL
// ======================================================================
//
// THE RAIL DRAWS WHATEVER ARRIVES. `stages` is er_case_types.stages,
// per-tenant JSONB, of any length — migration 053 deliberately put no
// CHECK on er_cases.current_stage so a tenant can name and order its own
// steps. There is no stage list in this file, no label lookup, no
// assumed count and no assumed order beyond the array's own. A case type
// with four stages and one with nine both render, and so does one with
// twenty.
//
// `stage_index` is null when the case sits on a key the tenant has since
// removed from its array. That is a real state, not an error, and it is
// rendered as one: the rail draws with no step marked current, because
// showing a plausible wrong position is worse than showing none.
//
// An EMPTY stages array happens when the tenant's configuration is
// malformed — routes._build_case_detail degrades to that deliberately
// rather than 500ing every case in the organisation. The rail is then
// omitted; the case still opens.

export default function CaseHeader({ detail }) {
  // The tenant's own word for each role (task 27). See
  // components/cases/roleLabels.js.
  const { label: roleLabel } = useRoleLabels()

  const {
    reference,
    title,
    case_type_label: typeLabel,
    current_stage: currentStage,
    status,
    opened_at: openedAt,
    target_close_at: target,
    stage_entered_at: stageEnteredAt,
    stages,
    stage_index: stageIndex,
    stage_count: stageCount,
    days_elapsed: elapsed,
    days_remaining: remaining,
    breached,
    in_reminder_window: approaching,
    paused_at: pausedAt,
    pause_reason: pauseReason,
    roles,
  } = detail

  const tone = breached ? 'clock-breach' : approaching ? 'clock-warn' : ''

  return (
    <div className={`panel anim-rise overflow-hidden ${tone}`}>
      <div className="px-5 py-5 sm:px-6">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="mono text-accent">{reference}</span>
          <Chip>{typeLabel}</Chip>
        </div>

        <h1 className="mt-2 font-head text-[22px] font-bold leading-tight tracking-tight">
          {title}
        </h1>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <Chip tone={status === 'closed' ? undefined : 'accent'}>{words(status)}</Chip>
          <Chip tone="blue">{words(currentStage)}</Chip>
          {/* Only when the two disagree. A paused case usually carries
              status "paused" as well, and two identical chips read as a
              rendering bug rather than as emphasis. */}
          {pausedAt && status !== 'paused' && <Chip tone="amber">paused</Chip>}
          {roles.map((r) => (
            <Chip key={r} title="Your role on this case">
              you: {roleLabel(r)}
            </Chip>
          ))}
        </div>

        {/* THE POLICY CLOCK. Every number here is the backend's. */}
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12px]">
          <Fact label="Opened" value={date(openedAt)} />
          <Fact label="In this stage since" value={date(stageEnteredAt)} />
          {target && (
            <Fact
              label="Target close"
              value={date(target)}
              tone={breached || approaching ? 'amber' : null}
            />
          )}
          <Fact label="Elapsed" value={pluralise(elapsed, 'working day', 'working days')} />
          {remaining != null && (
            <Fact
              label={remaining < 0 ? 'Overrun' : 'Remaining'}
              value={pluralise(Math.abs(remaining), 'working day', 'working days')}
              tone={breached || approaching ? 'amber' : null}
            />
          )}
        </div>

        {(breached || approaching) && (
          <div className="mt-4">
            <Notice
              tone="amber"
              icon={Timer}
              title={breached ? 'The target close date has passed' : 'The target close date is approaching'}
            >
              {breached
                ? 'This is the case’s own target under the policy that governs it, counted in working days by the service. It is not a legal deadline and passing it does not invalidate anything — but it is recorded, and a party can ask why.'
                : 'The case is inside its reminder window. The window and the target both come from the case type’s configuration.'}
            </Notice>
          </div>
        )}

        {/* A PAUSED CASE SHOWS THE REASON AND A FROZEN CLOCK.
            days_elapsed and days_remaining stop moving at paused_at and
            stay there until the case is resumed — the numbers above are
            already frozen, so this says so rather than implying the
            clock is still running. */}
        {pausedAt && (
          <div className="mt-3">
            <Notice tone="amber" icon={PauseCircle} title="This case is paused">
              <p>
                Paused on <span className="text-ink">{dateTime(pausedAt)}</span>. The
                clock is stopped: the elapsed and remaining figures above are frozen
                at that moment and will not move until the case is resumed.
              </p>
              {pauseReason && (
                <p className="mt-1.5">
                  <span className="text-faint">Reason given: </span>
                  <span className="text-ink">{pauseReason}</span>
                </p>
              )}
            </Notice>
          </div>
        )}
      </div>

      {stages.length > 0 && (
        <StageRail stages={stages} index={stageIndex} count={stageCount} />
      )}
    </div>
  )
}

function Fact({ label, value, tone }) {
  if (!value) return null
  return (
    <span>
      <span className="text-faint">{label} </span>
      <span className={tone === 'amber' ? 'text-amber' : 'text-ink'}>{value}</span>
    </span>
  )
}

function StageRail({ stages, index, count }) {
  return (
    <div className="border-t border-line bg-surface-2/60 px-5 py-4 sm:px-6">
      <div className="eyebrow mb-3">
        Stages · {count} configured for this case type
      </div>
      <ol className="flex gap-1.5 overflow-x-auto pb-1">
        {stages.map((s, i) => {
          const done = index != null && i < index
          const current = index != null && i === index
          return (
            // A CEILING AS WELL AS A FLOOR, SINCE TASK 26 MADE THIS PAGE
            // FULL WIDTH. flex-1 divides the row equally, so on a 2560px
            // window a FOUR-stage case type would give each step a
            // 600px slab carrying one short label — four captions
            // marooned in space, which reads as a broken layout rather
            // than a progress rail. maxWidth stops each step growing
            // past a size a label actually fills; the rail then sits
            // left-aligned in the panel and the remaining width is
            // honest empty space.
            //
            // NINE STAGES ARE UNAFFECTED at any width — they hit the
            // 96px floor and the existing overflow-x-auto long before
            // they reach this ceiling, which is the behaviour that was
            // already there and is deliberately not changed.
            <li
              key={s.key}
              className="min-w-0 flex-1"
              style={{ minWidth: 96, maxWidth: 240 }}
            >
              <div
                className={`h-[3px] rounded-full ${
                  current ? 'bg-accent' : done ? 'bg-accent/40' : 'bg-line'
                }`}
                style={current ? { boxShadow: '0 0 8px var(--accent-glow)' } : undefined}
              />
              <div className="mt-2">
                <div
                  className={`font-head text-[11.5px] font-semibold leading-snug ${
                    current ? 'text-accent' : done ? 'text-muted' : 'text-faint'
                  }`}
                >
                  {s.label}
                </div>
                <div className="mt-0.5 text-[10.5px] text-faint">
                  {pluralise(s.target_days, 'day', 'days')}
                </div>
              </div>
            </li>
          )
        })}
      </ol>
      {index == null && (
        <p className="mt-3 text-[11.5px] leading-relaxed text-amber">
          This case is on a stage that is not in its case type’s current list, so
          no position is marked. The stage list has been changed since the case
          reached it.
        </p>
      )}
    </div>
  )
}
